import { Injectable } from '@angular/core';

import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom, throwError } from 'rxjs';
import { Organization, SessionTimeout } from 'src/app/services/organization.model';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { catchError, map } from 'rxjs/operators';
import { User } from 'src/app/services/user';
import { UserDataService } from 'src/app/services/user-data-service';
import { OrgFtpFolderBrowseResult, OrgFtpService, OrgFtpServiceSaveRequest, OrgFtpServiceSaveResponse, OrgFtpServiceTestResult } from 'src/app/views/settings/organization-settings/ftp-setup/org-ftp-service.model';
import { FtpService } from 'src/app/domainObjects/dataConnector';

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
// Subject to hold and emit selected organization data
private selectedOrganizationSubject = new BehaviorSubject<string>('');
selectedOrganization$ = this.selectedOrganizationSubject.asObservable();  // Observable for subscribers

  constructor(private http: HttpClient, private environmentService: EnvironmentService, private userDataService:UserDataService) { }


  

   // Method to fetch the list of users using POST
   getOrganizations(): Observable<Organization[]> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Organization/getorganizations`;
  // You can pass any necessary body to the POST request (replace `{}` with the actual body if needed)
  return this.http.post<Organization[]>(apiUrl, {}).pipe(
    map((response: Organization[]) => {
      return response;
    })
  );
}
async createOrganization(organization: any): Promise<Organization> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Organization/insertorganization`;
  try {
    const response = await this.http.post<Organization>(apiUrl, organization).toPromise();
    return response; // Return the created organization response from API
  } catch (error) {
    console.error('Error creating organization:', error);
    throw error; // Re-throw the error to be handled by the caller
  }
}
async insertOrganizationSessionTimeout(sessionTimeout: any): Promise<SessionTimeout> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/insert-organization-session-timeout`;
  try {
    const response = await this.http.post<SessionTimeout>(apiUrl, sessionTimeout).toPromise();
    return response; // Return the created organization response from API
  } catch (error) {
    console.error('Error inserting session timeout data:', error);
    throw error; // Re-throw the error to be handled by the caller
  }
}

async updateOrganizationSessionTimeout(sessionTimeout: any): Promise<SessionTimeout> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/update-organization-session-timeout`;
  try {
    const response = await this.http.post<SessionTimeout>(apiUrl, sessionTimeout).toPromise();
    return response; // Return the created organization response from API
  } catch (error) {
    console.error('Error updating session timeout data:', error);
    throw error; // Re-throw the error to be handled by the caller
  }
}


getSessionTimeoutsByOrgId(organizationId: string): Observable<SessionTimeout[]> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Account/get-organization-session-timeout-by-org`;

  const params = new HttpParams().set('OrganizationId', organizationId);

  return this.http.get<SessionTimeout[]>(apiUrl, { params }).pipe(
    map((response: SessionTimeout[]) => response),
    catchError((error) => {
      console.error('Error fetching session timeouts:', error);
      return throwError(() => new Error('Error fetching session timeouts. Please try again.'));
    })
  );
}


// ✅ Add this method
/*async saveSessionTimeouts(organization: any): Promise<Organization> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Organization/insertorganization`;
  try {
    const response = await this.http.post<Organization>(apiUrl, organization).toPromise();
    return response; // Return the created organization response from API
  } catch (error) {
    console.error('Error creating organization:', error);
    throw error; // Re-throw the error to be handled by the caller
  }
}*/
async updateOrganization(organization: any): Promise<Organization> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Organization/updateorganization`;
  try {
    const response = await this.http.put<Organization>(apiUrl, organization).toPromise();
    return response; // Return the updated organization response from API
  } catch (error) {
    console.error('Error updating organization:', error);
    throw error; // Re-throw the error to be handled by the caller
  }
}


async changeOrganizationStatus(organization: any): Promise<Organization> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Organization/deactivate-organization`;
  try {
    const response = await this.http.post<Organization>(apiUrl, organization).toPromise();
    return response; // Return the updated organization response from API
  } catch (error) {
    console.error('Error updating organization:', error);
    throw error; // Re-throw the error to be handled by the caller
  }
}
// Add this method to the existing UserService
getOrganizationById(organizationId: string): Observable<Organization> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Organization/getorganizationbyid?`; 
  // Use HttpParams to set the query parameter
  const params = new HttpParams().set('Id', organizationId); 

  return this.http.post<Organization>(apiUrl, null, { params }).pipe( // Pass null for the body
    map((response: Organization) => response), // Return the fetched user data
    catchError((error) => {
      console.error('Error fetching organization:', error);
      return throwError(() => new Error('Error fetching organization. Please try again.'));
    })
  );
}


deleteOrganization(organizationId: string): Observable<Organization> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Organization/deleteorganization`; 

  // Use HttpParams to set the query parameter
  const params = new HttpParams().set('Id', organizationId);

  // Change the request from POST to DELETE
  return this.http.delete<Organization>(apiUrl, { params }).pipe(
    map((response: Organization) => response), // Return the response
    catchError((error) => {
      console.error('Error deleting Organization:', error);
      return throwError(() => new Error('Error deleting Organization. Please try again.'));
    })
  );
}

async checkOrganizationExists(organizationName: string, companyId: number): Promise<Organization | null> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Organization/checkorganizationexists`;
  const params = new HttpParams()
    .set('organizationname', organizationName)
    .set('companyId', companyId.toString());

  try {
    const response = await this.http.get<Organization>(apiUrl, { params }).toPromise(); // Deprecated in RxJS 7+
    return response;
  } catch (error) {
    console.error('Error checking organization existence:', error);
    return null;
  }
}

// Organization FTP services, for the organization currently selected in this
// client (login / Change Organization). The server checks the logged-in user is a
// member of that organization (and an admin for saves), so it needs the session
// cookie: withCredentials is set on just these requests rather than widening the
// shared credentials interceptor.
private selectedFtpOrgId(): number {
  return Number(this.userDataService.getSelectedOrganization()) || 0;
}

getOrgFtpServices(): Promise<OrgFtpService[]> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Organization/get-org-ftpservices`;
  const params = new HttpParams().set('orgId', this.selectedFtpOrgId());
  return firstValueFrom(this.http.get<OrgFtpService[]>(apiUrl, { params, withCredentials: true }));
}

insertOrgFtpService(request: OrgFtpServiceSaveRequest): Promise<OrgFtpServiceSaveResponse> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Organization/insert-org-ftpservice`;
  const body = { ...request, orgId: this.selectedFtpOrgId() };
  return firstValueFrom(this.http.post<OrgFtpServiceSaveResponse>(apiUrl, body, { withCredentials: true }));
}

updateOrgFtpService(request: OrgFtpServiceSaveRequest): Promise<OrgFtpServiceSaveResponse> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Organization/update-org-ftpservice`;
  const body = { ...request, orgId: this.selectedFtpOrgId() };
  return firstValueFrom(this.http.put<OrgFtpServiceSaveResponse>(apiUrl, body, { withCredentials: true }));
}

// Deletes one FTP service of the selected organization (admin-only, enforced server-side).
deleteOrgFtpService(id: number): Promise<{ status: boolean; message: string }> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Organization/delete-org-ftpservice`;
  const params = new HttpParams().set('Id', id).set('orgId', this.selectedFtpOrgId());
  return firstValueFrom(this.http.delete<{ status: boolean; message: string }>(apiUrl, { params, withCredentials: true }));
}

// Full connection settings (including the password) of one active service, in
// the FtpService shape ingest connectors pass downstream.
async getOrgFtpServiceConnection(id: number): Promise<FtpService> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Organization/get-org-ftpservice-connection`;
  const params = new HttpParams().set('id', id).set('orgId', this.selectedFtpOrgId());
  return FtpService.fromJSON(await firstValueFrom(this.http.get<FtpService>(apiUrl, { params, withCredentials: true })));
}

// The stored password, fetched only when an admin explicitly clicks "Show password"
// in the Edit FTP Service dialog (never part of the list response).
async getOrgFtpServicePassword(id: number): Promise<string> {
  const apiUrl = `${this.environmentService.activeAppBaseUrl}/api/Organization/get-org-ftpservice-password`;
  const params = new HttpParams().set('id', id).set('orgId', this.selectedFtpOrgId());
  const result = await firstValueFrom(this.http.get<{ password: string }>(apiUrl, { params, withCredentials: true }));
  return result?.password ?? '';
}

// Folders of an FTP service (for a Data Writer export directory), listed directly on
// the engine, which reads the service by id within the organization — the password
// is never sent to the browser.
browseOrgFtpServiceFolders(id: number): Promise<OrgFtpFolderBrowseResult> {
  const apiUrl = `${this.environmentService.activeEngineBaseUrl}/api/v1.0/engine/browseOrgFtpServiceFolders`;
  const body = { id, orgId: this.selectedFtpOrgId() };
  return firstValueFrom(this.http.post<OrgFtpFolderBrowseResult>(apiUrl, body));
}

// Tests the stored configuration directly on the engine, which reads the service
// (password and connection timeout included) by id within the organization — the
// password is not sent from the browser. Resolves once the engine has the result.
testOrgFtpService(id: number): Promise<OrgFtpServiceTestResult> {
  const apiUrl = `${this.environmentService.activeEngineBaseUrl}/api/v1.0/engine/testOrgFtpService`;
  const body = { id, orgId: this.selectedFtpOrgId() };
  return firstValueFrom(this.http.post<OrgFtpServiceTestResult>(apiUrl, body));
}

setOrganization(org: any): void {
  localStorage.setItem('selectedOrganization', JSON.stringify(org));
}
// Method to set the selected organization for the top header

setSelectedOrganization(organizationId: string): { organizationName: string; roleId: string | null } {
  // Get the signed-in user's organizations
  const organizations = this.userDataService.getUserOrgs();

  // Find the selected organization by matching IDs
  const selectedOrg = organizations.find(
    (org: { organizationId: string | number }) =>
      String(org.organizationId) === organizationId // Convert both to string for comparison
  );

  if (selectedOrg) {
    // Save the selected organization name to localStorage and notify subscribers
    this.userDataService.setSelectedOrganizationName(selectedOrg.organizationName);
    //localStorage.setItem('selectedOrganizationName', selectedOrg.organizationName);
    this.selectedOrganizationSubject.next(selectedOrg.organizationName); // Emit new organization name to subscribers

    // Return the selected organization name and role ID
    return {
      organizationName: selectedOrg.organizationName,
      roleId: selectedOrg.roleId || null, // Return roleId or null if not present
    };
  } else {
    // Return default values if organization is not found
    return {
      organizationName: '',
      roleId: null,
    };
  }
}


}


